import { heldObjectSocket } from "game-common/lpc/hand_sockets"
import { castingHandAttachments, castingGripAttachment, headAttachment } from "./body_attachments"
import { ActionType } from "game-common/character/character"
import { LpcSheet } from "game-common/lpc/lpc"
import { Coordinate, Entity, IdAware, IndexedMap, randomIntBetween } from "game-common/models"
import { Callback, degrees_to_radians, radians_to_cardinal_dir, randomId } from "game-common/util"
import {
    AnimatedSprite,
    Application,
    BitmapText,
    Container,
    Graphics,
    LoaderResource,
    Rectangle,
    Sprite,
    Texture,
    Ticker,
} from "pixi.js"

import { SpriteMeta } from "../entity_rendering_manager"
import { BaseRenderable } from "./base_renderable"
import { EntityHighlight, HighlightFrame } from "./entity_highlight"
import { HALF_FRAME, LpcJobResult, LpcRenderProps, lpcCompositeGenerator } from "./lpc_composite_generator"
import { LoadingLifecycle, spriteSheetLoader } from "./spritesheet_loader"
import { logger } from "../../../../logger"
import { Mechanics } from "game-common/mechanics/mechanics"
import { shatterSprite } from "../effects/sprite_shatter"
import { PlayerRing } from "./player_ring"
import { MemberStatusHalo, StatusHaloTier } from "./member_status_halo"
import { AnimationPoseDriver, BodyAnimationPose } from "../effects/animation_pose"
import { BonecallerPose } from "../effects/bonecaller_pose"

export const AnimationDictionaryTypes = [
    "walk-left",
    "walk-right",
    "walk-back",
    "walk-front",
    "idle-left",
    "idle-right",
    "idle-back",
    "idle-front",
    "cast-left",
    "cast-right",
    "cast-back",
    "cast-front",
    "thrust-left",
    "thrust-right",
    "thrust-back",
    "thrust-front",
    "slash-left",
    "slash-right",
    "slash-back",
    "slash-front",
    "shoot-left",
    "shoot-right",
    "shoot-back",
    "shoot-front",
    "hurt-front",
    "activate",
    "deactivate",
    "inactive",
    "active",
] as const

const AnimationTypeToActionType: Record<AnimationDictionaryType, ActionType> = {
    activate: "activate",
    deactivate: "deactivate",
    inactive: "inactive",
    active: "activate",
    "walk-left": "walk",
    "walk-right": "walk",
    "walk-back": "walk",
    "walk-front": "walk",
    "idle-left": "idle",
    "idle-right": "idle",
    "idle-back": "idle",
    "idle-front": "idle",
    "cast-left": "cast",
    "cast-right": "cast",
    "cast-back": "cast",
    "cast-front": "cast",
    "thrust-left": "thrust",
    "thrust-right": "thrust",
    "thrust-back": "thrust",
    "thrust-front": "thrust",
    "slash-left": "slash",
    "slash-right": "slash",
    "slash-back": "slash",
    "slash-front": "slash",
    "shoot-left": "shoot",
    "shoot-right": "shoot",
    "shoot-back": "shoot",
    "shoot-front": "shoot",
    "hurt-front": "hurt",
}

export type AnimationDictionaryType = (typeof AnimationDictionaryTypes)[number]

/**
 * A pose the sprite HOLDS, against everything else that would otherwise choose its animation.
 *
 * Every other action is transient -- a walk ends when the feet stop, an attack returns to idle when
 * it completes, idle is simply what is left over. A held pose is a STATE the body is in, and stays
 * in until the server says otherwise, so the walk/idle driver and the facing-change handler both
 * have to stand aside for it (see doMovement and changeDirection).
 */
export type HeldPose = "downed"

/**
 * Which LPC animation depicts each held pose.
 *
 * `downed` is the universal sheet's `hurt` row, which is not a flinch but a COLLAPSE: six frames of
 * a body sinking to the ground, ending face-down. Held on that last frame, it is the fallen pose.
 *
 * Two things about that row shape everything below. It exists FRONT-FACING ONLY -- there is no
 * `hurt-left`, so a fallen body cannot turn -- and every humanoid LPC sheet in the game has it
 * (body, hair, every torso/legs/feet/cape/shield), so a fallen character keeps their whole
 * wardrobe. The non-humanoid sheets (rabbit, chicken, slime, ...) have no `hurt` row at all, which
 * is why LpcComposite only ever asks for this pose on a humanoid race.
 */
const HELD_POSE_ANIMATION: Record<HeldPose, AnimationDictionaryType> = {
    downed: "hurt-front",
}

const HELD_POSE_ANIMATIONS: AnimationDictionaryType[] = Object.values(HELD_POSE_ANIMATION)

export const isHeldPoseAnimation = (animation: AnimationDictionaryType): boolean =>
    HELD_POSE_ANIMATIONS.includes(animation)

export type LpcAnimationRenderMeta = Record<AnimationDictionaryType, SpriteMeta>

// Defined alongside the generator, whose cache key it feeds; re-exported here because this is
// where callers have always imported it from.
export type { LpcRenderProps }

export interface LpcSpriteRenderMeta {
    alwaysShowLabel?: boolean
    disallowMoveAttackAnimation?: boolean
    renderMeta?: Partial<LpcAnimationRenderMeta>
    attackAction?: ActionType
    angle?: number
    descriptors: LpcSheet[]
    hasHalo?: boolean
    isSubmerged?: boolean
    heldObject?: LpcRenderProps["heldObject"]
    hasShadow?: boolean
    // Sole line relative to the centre of the body, expressed in canonical 64px frame space.
    feetY?: number
    bodyFrame?: { w: number; h: number }
    groundAnchor?: { x: number; y: number }
    placement?: { x: number; y: number }
    // Marks this body as being driven by another real player (see PlayerRing). Never set for the
    // client's own character -- you already know where you are.
    hasPlayerRing?: boolean
    id?: string
    priority?: boolean
    loadObserver?: LoadObserver
    defaultAction?: ActionType
    // The pose this body is ALREADY in when its layers are built, if any. Passed at construction
    // rather than set afterwards so a body that is down before it is first drawn never flashes a
    // standing frame -- and so the pose survives a paperdoll rebuild, which throws the sprite away
    // and builds another (see LpcComposite.rebuildLayers).
    heldPose?: HeldPose
}

// Where the soles sit INSIDE a baked composite frame.
//
// The body is always a 64px LPC frame centred in whatever the composite baked to, so its feet are
// a FIXED distance below the frame's centre -- they do not scale with the frame. An oversize slash
// bakes at 192x192 with that same 64px body in the middle, so treating the feet as a proportion of
// the frame height (`fh * feetY / 64`) drops them 56px too low for the length of the swing, which
// is what put the projected shadow well below the character's feet mid-attack.
//
// Paired with the generator's OVERSIZE_OFFSET: that shifts the oversize frame so the body lands
// where the 64px frames put it, and this puts the sole line back at the same place inside it, so
// `animation.y + soleLineInFrame(...)` is the same ground line for every action.
export { soleLineInFrame } from "./shadow_projection"
import { anchoredShadowProjection } from "./shadow_projection"
import { bodyFootPosition } from "game-common/lpc/body_placement"

const defaultRenderMeta: LpcAnimationRenderMeta = {
    activate: {
        scale: 1,
        speed: 0.25,
    },
    deactivate: {
        scale: 1,
        speed: 0.25,
    },
    inactive: {
        scale: 1,
        animated: false,
    },
    active: {
        scale: 1,
    },
    "walk-left": {
        scale: 1,
        speed: 0.25,
    },
    "walk-right": {
        scale: 1,
        speed: 0.25,
    },
    "walk-back": {
        scale: 1,
        speed: 0.25,
    },
    "walk-front": {
        scale: 1,
        speed: 0.25,
    },
    "idle-left": {
        scale: 1,
    },
    "idle-right": {
        scale: 1,
    },
    "idle-back": {
        scale: 1,
    },
    "idle-front": {
        scale: 1,
    },
    "cast-left": { scale: 1, speed: 0.2 },
    "cast-right": { scale: 1, speed: 0.2 },
    "cast-back": { scale: 1, speed: 0.2 },
    "cast-front": { scale: 1, speed: 0.2 },
    "thrust-left": {
        scale: 1,
        speed: 0.55,
    },
    "thrust-right": {
        scale: 1,
        speed: 0.55,
    },
    "thrust-back": {
        scale: 1,
        speed: 0.55,
    },
    "thrust-front": {
        scale: 1,
        speed: 0.55,
    },
    "slash-left": {
        scale: 1,
        speed: 0.25,
    },
    "slash-right": {
        scale: 1,
        speed: 0.25,
    },
    "slash-back": {
        scale: 1,
        speed: 0.25,
    },
    "slash-front": {
        scale: 1,
        speed: 0.25,
    },
    "shoot-left": {
        scale: 1,
        speed: 0.85,
    },
    "shoot-right": {
        scale: 1,
        speed: 0.85,
    },
    "shoot-back": {
        scale: 1,
        speed: 0.85,
    },
    "shoot-front": {
        scale: 1,
        speed: 0.85,
    },
    "hurt-front": {
        scale: 1,
    },
}

const animationToAction = (animation: AnimationDictionaryType): ActionType => animation.split("-")[0] as ActionType

const isDirectional = (type: AnimationDictionaryType) => type.includes("-")

const isAttackAnimation = (animationName: AnimationDictionaryType) =>
    ["shoot", "thrust", "slash"].includes(animationToAction(animationName))

const isActivationAnimation = (animationName: AnimationDictionaryType) =>
    ["activate", "deactivate"].includes(animationToAction(animationName))

const isWalkAnimation = (animationName: AnimationDictionaryType) => ["walk"].includes(animationToAction(animationName))

const extractDirection = (animation: AnimationDictionaryType) => animation.split("-")[1]

type AnimationLoadingState = LoadingLifecycle | "failed"

interface ActionListener extends IdAware {
    callback: Callback<void>
    callbackId?: string
}

interface CallbackState {
    entityId: string
    callback: Callback<void>
    animationName: string
    actionType: ActionType
    createdAt: number
    status: "pending" | "executed" | "cancelled"
}

const actionTypeToAnimationAction = (actionType: ActionType): AnimationDictionaryType => {
    if (actionType === "activate") {
        return "activate"
    }
    if (actionType === "deactivate") {
        return "deactivate"
    }
    if (actionType === "inactive") {
        return "inactive"
    }
    if (actionType === "active") {
        return "active"
    }
}

const isLooped = (animation: AnimationDictionaryType) => {
    if (isAttackAnimation(animation) || isActivationAnimation(animation)) {
        return false
    }

    // A held pose is a one-shot that STOPS on its final frame. Looping it would replay the collapse
    // over and over -- a body falling down, standing up and falling again -- instead of lying
    // there, which is the whole of what the pose depicts.
    if (isHeldPoseAnimation(animation)) {
        return false
    }

    return true
}

export class LoadObserver {
    doneCallback: Callback<void>
    jobs: Set<string> = new Set()
    completed: boolean

    constructor(doneCallback: Callback<void>) {
        this.doneCallback = doneCallback
    }

    offer = (job: string) => {
        this.jobs.add(job)
    }

    complete = (job: string) => {
        this.jobs.delete(job)
        if (this.completed) {
            return
        }

        if (this.jobs.size < 1) {
            this.completed = true
            this.doneCallback()
        }
    }

    clear = () => {
        this.jobs.clear()
        this.completed = false
    }
}

// A sprite that participates in periodic callback maintenance. The ticker only needs these two
// operations, so it stays decoupled from the full LpcCompositeSprite type.
interface PeriodicCallbackMaintenance {
    checkForMissingCallbacks(): void
    cleanupOrphanedCallbacks(): void
}

// Single shared driver for every LpcCompositeSprite's periodic callback maintenance. Each sprite
// used to create its OWN setInterval(100ms) + setInterval(10s); with many sprites on screen that
// was dozens of independent timer wake-ups per second on the main thread -- a real cost on low-end
// CPUs. One shared pair of timers iterating the registered set is far cheaper, and each sprite's
// check early-returns when it has no active callbacks. The timers exist only while >=1 sprite is
// registered, so an empty scene schedules nothing.
class LpcSpriteMaintenanceTicker {
    private sprites: Set<PeriodicCallbackMaintenance> = new Set()
    private missingTimer: any
    private orphanedTimer: any

    register = (sprite: PeriodicCallbackMaintenance) => {
        this.sprites.add(sprite)
        if (this.missingTimer === undefined) {
            this.missingTimer = setInterval(this.runMissingChecks, 100)
        }
        if (this.orphanedTimer === undefined) {
            this.orphanedTimer = setInterval(this.runOrphanedCleanup, 10000)
        }
    }

    unregister = (sprite: PeriodicCallbackMaintenance) => {
        if (!this.sprites.delete(sprite)) {
            return
        }
        if (this.sprites.size === 0) {
            clearInterval(this.missingTimer)
            clearInterval(this.orphanedTimer)
            this.missingTimer = undefined
            this.orphanedTimer = undefined
        }
    }

    // Iterate a snapshot and isolate each sprite so one throwing check can't stop maintenance for
    // the rest (and can't kill the shared interval).
    private runMissingChecks = () => {
        Array.from(this.sprites).forEach(s => {
            try {
                s.checkForMissingCallbacks()
            } catch (e) {
                // best-effort maintenance
            }
        })
    }

    private runOrphanedCleanup = () => {
        Array.from(this.sprites).forEach(s => {
            try {
                s.cleanupOrphanedCallbacks()
            } catch (e) {
                // best-effort maintenance
            }
        })
    }
}

const lpcSpriteMaintenanceTicker = new LpcSpriteMaintenanceTicker()

export class LpcCompositeSprite extends BaseRenderable {
    spriteAngle: number = 0

    currentColor: number
    labelText: BitmapText

    container: Container

    lastX: number
    lastY: number

    action: AnimationDictionaryType = "idle-front"
    prevAction: AnimationDictionaryType = "idle-front"

    animations: Record<AnimationDictionaryType, LpcJobResult> = {} as Record<AnimationDictionaryType, LpcJobResult>

    // Set once this sprite has been discarded, so a late async generate callback disposes its
    // clone instead of adopting it (and leaking a cache reference).
    private spriteReleased = false
    // Cache key retained at request time for each action we've prepared (see doPreparation). Released
    // once per entry in cleanup() -- covers adopted, still-loading, and failed actions alike.
    private preparedActionKeys = new Map<AnimationDictionaryType, string>()

    idleTimeout: any

    disallowMoveAttackAnimation: boolean
    renderMeta: LpcAnimationRenderMeta
    renderProps: LpcRenderProps
    spriteRootName: string

    facingDir: string = "front"
    descriptors: LpcSheet[]

    spritesheetResource: LoaderResource

    attackAction: ActionType = "idle"

    animationDictionaryTypeLoadingState: Map<AnimationDictionaryType, AnimationLoadingState> = new Map()
    app: Application
    initialized: boolean
    identifier: string

    creationTs: number
    halo: Graphics
    shadow: Graphics
    // The "another player is here" ground marker, present only on remote player bodies.
    playerRing?: PlayerRing

    // Show or hide the ground ring without rebuilding anything. Hiding rather than disposing: an
    // invader's ring comes back the moment they are their own person again, and a sprite that threw
    // the ring away would have to rebuild its layers to get one.
    setPlayerRingVisible(visible: boolean): void {
        if (this.playerRing) {
            this.playerRing.visible = visible
        }
    }

    // The "this camp member needs something" ground halo. Built with the shadows (see the
    // constructor) and lit or put out from here, because a member's status changes many times a
    // minute and rebuilding a paperdoll for each one is unthinkable.
    statusHalo?: MemberStatusHalo

    setStatusHaloTier(tier?: StatusHaloTier): void {
        this.statusHalo?.setTier(tier)
    }
    // Projected silhouette shadow (default on; window.spriteShadow = false → foot ellipse). Shares the
    // active animation's baked frame textures -- no extra bake/VRAM. Updated each frame on Ticker.shared.
    spriteShadow?: Sprite
    private feetY: number = 60
    private groundAnchor = { x: 32, y: 60 }
    private bodyFrame = { w: 64, h: 64 }
    private placement = { x: 0, y: 0 }
    private spriteShadowTicking = false
    // Hover outline (see setHighlighted). Created on first hover; only the pointed-at entity has one.
    private highlight?: EntityHighlight
    id: string
    priority: boolean

    actionCompleteListeners: Map<ActionType, IndexedMap<ActionListener>> = new Map()

    // Enhanced callback management
    private callbackStates = new Map<string, CallbackState>()
    private activeCallbacks = new Set<string>()
    private currentAnimationCallbacks = new Set<string>()

    // Whether this sprite is registered with the shared maintenance ticker (see startCleanupTimer).
    private registeredWithTicker: boolean = false

    loadObserver?: LoadObserver

    defaultAction?: ActionType

    // The pose this body is HELD in, or undefined when it is free to walk, idle and attack as
    // usual. See HeldPose -- while it is set, doMovement and changeDirection leave the sprite alone.
    private heldPose?: HeldPose
    private bodyPoseDriver = new BonecallerPose()
    private animationPoseDriver = new AnimationPoseDriver()
    private bodyPose?: BodyAnimationPose
    // True while the NEXT activation of the held pose should play its animation through rather than
    // snap to the final frame: we watched this body go down, so the fall is worth showing. Set by
    // setHeldPose and consumed by the activation it was set for.
    private playHeldPoseEntry = false
    // True while the body is GETTING UP: the pose's own animation running backwards, from the
    // ground back to standing. `heldPose` stays set throughout, so every guard that protects a held
    // pose protects the rise too; it is cleared when the rise lands (see releaseHeldPose).
    private rising = false
    // Whatever `onComplete` the pose's animation carried before the rise borrowed it (the adopt
    // path hangs callback cleanup there). Put back when the rise ends, however it ends.
    private riseRestoreOnComplete?: () => void

    constructor({
        descriptors,
        renderMeta = defaultRenderMeta,
        attackAction,
        angle,
        hasHalo,
        hasShadow,
        feetY,
        bodyFrame = { w: 64, h: 64 },
        groundAnchor,
        placement = { x: 0, y: 0 },
        hasPlayerRing,
        id,
        priority,
        loadObserver,
        isSubmerged,
        heldObject,
        defaultAction,
        heldPose,
    }: LpcSpriteRenderMeta) {
        super()

        // TEST: Log immediately to verify logging is working
        logger.info(
            { tags: ["callback", "animation"] },
            `LPC Composite Sprite created for entity ${id}, action: ${this.action}`,
        )

        this.id = id
        this.priority = priority

        this.renderMeta = { ...defaultRenderMeta, ...renderMeta }
        this.renderProps = {
            bodyFrame,
            isSubmerged,
            heldObject,
        }
        this.attackAction = attackAction

        this.spriteAngle = angle
        this.facingDir = this.calcDir(angle)

        this.container = new Graphics()
        this.addChild(this.container)
        this.loadObserver = loadObserver

        if (hasHalo) {
            const halo = new Graphics()
            halo.alpha = 0.5
            halo.beginFill(0xffffff)
            halo.drawCircle(32, 25, 13)
            halo.endFill()
            this.halo = halo
            this.container.addChild(halo)
        }

        // The ground line this body stands on, shared by the foot shadow and the player ring. A
        // submerged body sits higher in its frame, so its feet are higher too.
        this.feetY = feetY ?? (isSubmerged ? 55 : 60)
        this.placement = placement
        this.bodyFrame = bodyFrame
        const feet = this.groundAnchor = groundAnchor ?? bodyFootPosition(bodyFrame.w, bodyFrame.h, this.feetY)

        if (hasShadow) {
            const shadow = new Graphics()
            shadow.alpha = 0.2
            shadow.beginFill(0x000000)
            shadow.drawEllipse(feet.x, feet.y, 16, 8)
            shadow.endFill()
            this.shadow = shadow
            this.container.addChild(shadow)

            // Projected silhouette shadow -- on by default (window.spriteShadow = false falls back to
            // the ellipse). Sits behind the character; the per-frame update swaps which one is visible.
            const spriteShadow = new Sprite(Texture.EMPTY)
            spriteShadow.tint = 0x000000
            spriteShadow.visible = false
            this.spriteShadow = spriteShadow
            this.container.addChild(spriteShadow)

            Ticker.shared.add(this.updateSpriteShadow)
            this.spriteShadowTicking = true
        }

        if (hasShadow) {
            // Built here, with the shadows, and NOT lazily when a status first arrives -- the
            // container has no sortableChildren, so a child's z-order is the order it was added in,
            // and anything created later would land on top of the body instead of under it. Costs an
            // invisible Graphics with no geometry in it until something is actually wrong with this
            // member; see MemberStatusHalo.
            const halo = new MemberStatusHalo()
            halo.x = feet.x
            halo.y = feet.y
            this.statusHalo = halo
            this.container.addChild(halo)
        }

        if (hasPlayerRing) {
            // Added after both shadows so neither darkens the marker, and before the animation layers
            // (which arrive later, see doPreparation) so the body occludes its middle and the character
            // reads as standing inside the ring rather than in front of a decal.
            const ring = new PlayerRing()
            ring.x = feet.x
            ring.y = feet.y
            this.playerRing = ring
            this.container.addChild(ring)
        }

        this.descriptors = descriptors
        this.creationTs = Date.now()
        this.defaultAction = defaultAction
        // Set before initalize() runs so the FIRST action this sprite ever prepares is the pose
        // itself, rather than an idle frame it would immediately replace.
        this.heldPose = heldPose
    }

    // Per-frame: draw the active animation's current frame a second time, projected onto the ground
    // along the sun vector, as a real pose-accurate shadow. Reuses the entity's own baked frame textures
    // (shared, refcounted by the generator) -- no new bake, no filter. The projection is a planar shadow
    // matrix: every pixel of the sprite's height above the feet is displaced by (dirX, dirY) on the
    // ground, so (dirX, dirY) IS the sun angle. Defaults cast a shallow shadow toward the lower-left to
    // match the map's baked bush/tree shadows. window.spriteShadow = false falls back to the ellipse;
    // window.spriteShadow{DirX,DirY,Alpha} tune it live without a reload.
    updateSpriteShadow = () => {
        const shadow = this.spriteShadow
        if (!shadow) {
            return
        }

        // On by default; set window.spriteShadow = false to fall back to the foot ellipse.
        const w = window as any
        const on = w.spriteShadow !== false

        // Swap which shadow style is visible.
        if (this.shadow) {
            this.shadow.visible = !on
        }
        if (!on) {
            if (shadow.visible) {
                shadow.visible = false
                shadow.texture = Texture.EMPTY
            }
            return
        }

        const job = this.animations[this.action]
        const active = job?.animation ?? job?.sprite
        if (!active || !active.visible) {
            shadow.visible = false
            return
        }
        const tex = job.animation ? job.animation.textures[job.animation.currentFrame] as Texture : job.sprite.texture
        if (!tex || !tex.valid) {
            shadow.visible = false
            return
        }

        // Sun vector: ground displacement per pixel of sprite height. dirX < 0 leans left, dirY > 0
        // stretches "south". A shallow lower-left cast (|dirX| > dirY) matches the baked bush/tree art.
        const dirX = typeof w.spriteShadowDirX === "number" ? w.spriteShadowDirX : -0.23
        const dirY = typeof w.spriteShadowDirY === "number" ? w.spriteShadowDirY : 0.13
        const alpha = typeof w.spriteShadowAlpha === "number" ? w.spriteShadowAlpha : 0.3

        shadow.texture = tex
        shadow.anchor.set(0, 0)
        shadow.alpha = alpha
        shadow.transform.setFromMatrix(anchoredShadowProjection(
            { x: (this.groundAnchor.x - active.x) / active.scale.x,
              y: (bodyFootPosition(this.bodyFrame.w, this.bodyFrame.h, this.feetY).y - active.y) / active.scale.y },
            this.groundAnchor, active.scale.x, active.scale.y, dirX, dirY))
        shadow.visible = true
    }

    // The frame EntityHighlight traces for the hover outline: the active animation's current baked
    // texture plus the transform it is drawn with, so the outline lands exactly on the pose (including
    // any oversize offset already baked into active.x/scale) and follows the animation frame by frame.
    // Null while the sheets are still loading or when this sprite has no art of its own -- the caller
    // then falls back to a footprint ring.
    highlightFrame = (): HighlightFrame | null => {
        const result = this.animations[this.action]
        const active: any = result?.animation ?? result?.sprite
        if (!active || !active.visible) {
            return null
        }
        const texture: Texture = result.fragmentBase()
        if (!texture?.valid) {
            return null
        }
        return {
            texture,
            x: active.x,
            y: active.y,
            scaleX: active.scale.x,
            scaleY: active.scale.y,
            anchorX: active.anchor?.x ?? 0,
            anchorY: active.anchor?.y ?? 0,
        }
    }

    heldAttackGripWorld = (race: string) => {
        const frame = this.animations[this.action]?.animation?.currentFrame ?? 0
        const point = this.action.startsWith("slash-") && race === "skeleton" ?
            heldObjectSocket(this.action, frame, "male") : castingGripAttachment(race, this.action, frame)
        if (!point || !this.initialized) return undefined
        const world = this.toGlobal({ x: point.x + this.placement.x, y: point.y + this.placement.y })
        const hands = castingHandAttachments(race, this.action, frame)?.map(hand =>
            this.toGlobal({ x: hand.x + this.placement.x, y: hand.y + this.placement.y }))
        return { x: world.x, y: world.y, scale: Math.hypot(this.worldTransform.a, this.worldTransform.b), hands }
    }

    headAttachmentWorld = (race: string) => {
        const result = this.animations[this.action]
        const frame = result?.animation?.currentFrame ?? 0
        const point = headAttachment(race, this.action, frame)
        if (!point || !this.initialized) return undefined
        return this.toGlobal({ x: point.x + this.placement.x, y: point.y + this.placement.y })
    }

    setHighlighted = (on: boolean): boolean => {
        if (!this.highlight) {
            if (!on) {
                return true
            }
            this.highlight = new EntityHighlight({
                artContainer: this.container,
                frame: this.highlightFrame,
            })
        }
        this.highlight.set(on)
        return true
    }

    logme = (msg: string, m2: any = "", m3: any = "") => {
        // console.log(this.creationTs, "@@@", this.identifier, msg, m2, m3)
    }

    initalize = (id: string, angle: number) => {
        this.identifier = id

        const f = this.calcDir(angle)
        // A body that is already in a held pose builds straight into it. Going through idle first
        // would bake an action nothing ever shows, and (worse) put a standing frame on screen for
        // the frame or two before the pose's own sheets finish loading.
        const action =
            (this.heldPose && HELD_POSE_ANIMATION[this.heldPose]) ||
            actionTypeToAnimationAction(this.defaultAction) ||
            (("idle-" + f) as any)
        let descriptorCountdown = this.descriptors.length
        this.logme("initializing sheets", id)

        const s = Date.now()
        this.loadObserver?.offer(action)

        // No sheets to wait on: the per-descriptor countdown below would never reach zero, leaving
        // the sprite permanently uninitialized (and blank). Initialize straight away instead.
        if (descriptorCountdown <= 0) {
            this.initialized = true
            this.loadObserver?.complete(action)
            this.startCleanupTimer()
            this.activateAnimation(action, true)
            return
        }

        this.descriptors.forEach((descriptor, i) => {
            const sheetId = `lpc/spritesheets/${descriptor.entityId}.json`
            const url = `assets/${sheetId}`
            this.logme("loading", url)

            spriteSheetLoader.load(url, _ => {
                descriptorCountdown -= 1
                if (descriptorCountdown <= 0) {
                    this.initialized = true
                    this.logme("@@@ all sheets loaded", action)
                    this.loadObserver?.complete(action)

                    // Start cleanup timer for orphaned callbacks
                    this.startCleanupTimer()

                    const elapsed = Date.now() - s
                    this.activateAnimation(action, true)
                }
            })
        })
    }

    recolor = (color: number) => {
        if (this.currentColor === color) {
            return
        }
        this.currentColor = color

        const source = this.animations[`${this.action}`]
        if (source) {
            source.recolor(color)
        }
    }

    calcDir(angle: number): string {
        return radians_to_cardinal_dir(angle)
    }

    doMovement(entity: Entity): void {
        const { movement } = entity
        if (!movement) {
            return
        }

        // A body already WALKING cannot still be getting up. The server frees the legs on the same
        // message that lifts `downed`, so a player who had a direction held is moving before the
        // reverse animation is a third of the way through -- and holding the prone frames out would
        // slide a body face-down across the ground. Movement wins: cut the rise short and let the
        // ordinary driver below pick up the walk on this very frame.
        // `lastX !== undefined` guards the same thing it guards in the walk branch below: on the
        // very first frame a body is drawn there is no previous position, so every coordinate
        // "differs" and the rise would be cut short before it drew a frame.
        if (
            this.rising &&
            this.lastX !== undefined &&
            (entity.location.x !== this.lastX || entity.location.y !== this.lastY)
        ) {
            this.releaseHeldPose()
        }

        if (this.heldPose) {
            // A held pose OWNS this sprite. Everything below would take it away: the walk/idle
            // driver stands a fallen body up within 50ms of it stopping, and changeDirection asks
            // for a `hurt-left` that does not exist in any LPC sheet. It owns the sprite while the
            // body is getting up too -- `heldPose` is not cleared until the rise lands.
            //
            // The bookkeeping those branches do is still kept up to date, so that letting go of the
            // pose resumes from where the body actually is rather than from wherever it was when it
            // went down: the facing it would have had, and a position that does not read as a jump.
            this.facingDir = this.calcDir(movement.angle)
            this.spriteAngle = movement.angle
            this.lastX = entity.location.x
            this.lastY = entity.location.y
            return
        }

        const previousPose = this.bodyPose
        this.bodyPose = this.animationPoseDriver.update(entity, Date.now()) || this.bodyPoseDriver.update(entity, Date.now())
        if (this.bodyPose) {
            if (this.idleTimeout !== undefined) { clearTimeout(this.idleTimeout); this.idleTimeout = undefined }
            this.facingDir = extractDirection(this.bodyPose.action as AnimationDictionaryType)
            this.lastX = entity.location.x; this.lastY = entity.location.y
            this.activateAnimation(this.bodyPose.action as AnimationDictionaryType)
            this.applyBodyPose()
            return
        }
        if (previousPose) this.activateAnimation(`idle-${this.facingDir}` as AnimationDictionaryType)

        if (this.spriteAngle !== movement.angle) {
            const facingDir = this.calcDir(movement.angle)
            if (facingDir !== this.facingDir) {
                // Enhanced direction change handling
                this.changeDirection(facingDir)
            }
            this.facingDir = facingDir
        }
        this.spriteAngle = movement.angle

        const { x, y } = entity.location

        if (x !== this.lastX || y !== this.lastY) {
            if (this.idleTimeout !== undefined) {
                clearTimeout(this.idleTimeout)
                this.idleTimeout = undefined
            }

            if (this.lastX !== undefined && this.lastY !== undefined && !isAttackAnimation(this.action)) {
                this.activateAnimation(`walk-${this.facingDir}` as AnimationDictionaryType)
            }

            this.lastX = x
            this.lastY = y
        } else {
            if (this.idleTimeout === undefined) {
                this.idleTimeout = setTimeout(() => {
                    if (isAttackAnimation(this.action)) {
                        return
                    }
                    if (!Mechanics.entity.body.isStationary(entity)) {
                        this.activateAnimation(`idle-${this.facingDir}` as AnimationDictionaryType)
                    }
                }, 50)
            }
        }
    }

    postAnimation = (animation: AnimationDictionaryType): AnimationDictionaryType => {
        if (isAttackAnimation(animation)) {
            return `idle-${extractDirection(this.action)}` as AnimationDictionaryType
        }
        if (animation === "activate") {
            return "active"
        }
        if (animation === "deactivate") {
            return "inactive"
        }
    }

    doPreparation = (action: AnimationDictionaryType) => {
        this.logme("@@@ kicked off", action)

        this.animationDictionaryTypeLoadingState.set(action, "loading")

        // Retain this loadout's cache key NOW, at request time -- generation is async/time-budgeted
        // and the baked textures are shared across sprites, so retaining only on adopt (below) leaves
        // a window where eviction could destroy textures this sprite is about to display. Released
        // once per prepared action in cleanup().
        if (!this.preparedActionKeys.has(action)) {
            const key = lpcCompositeGenerator.computeKey(this.descriptors, action, this.renderProps)
            this.preparedActionKeys.set(action, key)
            lpcCompositeGenerator.retainKey(key)
        }

        lpcCompositeGenerator.generate(
            this.priority,
            action,
            this.renderMeta[action],
            this.renderProps,
            this.descriptors,
            (result: LpcJobResult) => {
                if (result) {
                    // Sprite was discarded before this async result arrived: dispose the orphan
                    // clone rather than adopting it into a detached container.
                    if (this.spriteReleased) {
                        lpcCompositeGenerator.abandon(result)
                        return
                    }

                    const animation = result.animation
                    const sprite = result.sprite

                    if (animation) {
                        animation.x += this.placement.x
                        animation.y += this.placement.y
                        this.container.addChild(animation)
                    }
                    if (sprite) {
                        sprite.x += this.placement.x
                        sprite.y += this.placement.y
                        this.container.addChild(sprite)
                    }

                    const postAnimation = this.postAnimation(action)

                    this.logme("caching", action)
                    if (animation) {
                        animation.loop = isLooped(action)
                        const listeners = this.actionCompleteListeners.get(animationToAction(action))
                        if (listeners?.size() > 0) {
                            const totalFrames = animation.totalFrames

                            animation.onFrameChange = (frame: number) => {
                                const pct = frame / (totalFrames - 1)

                                if (pct > 0.5) {
                                    // Enhanced callback execution with state tracking
                                    this.executeCallbacks(listeners, animationToAction(action))
                                }
                            }
                        }

                        if (postAnimation) {
                            animation.onComplete = () => {
                                this.activateAnimation(postAnimation)
                            }
                        } else {
                            // Add completion handler for cleanup
                            animation.onComplete = () => {
                                this.cleanupAnimationCallbacks(action)
                            }
                        }
                    }

                    if (sprite) {
                        const listeners = this.actionCompleteListeners.get(animationToAction(action))
                        if (listeners?.size() > 0) {
                            listeners.forEach(listener => {
                                listener.callback()
                                listeners.remove(listener.entityId)
                            })
                        }
                    }

                    this.animations[action] = result
                    // No retain here: the key was already retained at request time (doPreparation).
                    // Retain-at-request closes the async request->adopt window that retain-on-adopt left
                    // open (eviction could destroy the shared textures before this clone went live).
                    this.logme("is all prepared for", action)
                    this.animationDictionaryTypeLoadingState.set(action, "loaded")
                    this.activateAnimation(action, true)

                    if (sprite && postAnimation) {
                        this.activateAnimation(postAnimation)
                    }
                } else {
                    this.logme("! could not prepare", action)
                    this.animationDictionaryTypeLoadingState.set(action, "failed")
                }
            },
            // Lets the generator drop this job cheaply if the sprite is discarded (entity torn down
            // on a room change) before the job runs, so it never bakes a texture nobody wants.
            () => this.spriteReleased,
        )
    }

    prepare = (updatedAction: AnimationDictionaryType) => {
        this.logme("@@@ prepare", updatedAction)
        if (this.animations[updatedAction]) {
            this.logme("@@@ already exists", updatedAction)
            // already exists
            return
        }
        const loadState = this.animationDictionaryTypeLoadingState.get(updatedAction)
        if (loadState && loadState !== "loaded" && loadState !== "failed") {
            this.logme("@@@", updatedAction, "still in progress")
            // still in progress
            return
        }

        if (loadState === "failed") {
            this.logme("@@@", updatedAction, "failed")
            return
        }

        // kick it off
        this.doPreparation(updatedAction)
    }

    /**
     * True when activating `action` should freeze it on its last frame instead of playing it: the
     * body is held in this pose, and we did not watch it arrive there.
     */
    private holdsFinalFrame = (action: AnimationDictionaryType): boolean =>
        !!this.heldPose && action === HELD_POSE_ANIMATION[this.heldPose] && !this.playHeldPoseEntry

    /**
     * Put this body into a held pose, or let it back out of one. Idempotent: re-asserting the pose
     * already held does nothing, which matters because the caller re-reads it every frame.
     *
     * `animate` chooses between the two ways a body arrives in a pose. Watching someone fall should
     * play the collapse; walking up to someone who fell before you got there should not, and nor
     * should a paperdoll rebuild -- the body would stand up and fall over again every time its
     * inventory changed.
     */
    setHeldPose = (pose: HeldPose | undefined, animate: boolean = true) => {
        if (pose) {
            const alreadyHeld = pose === this.heldPose
            if (alreadyHeld && !this.rising) {
                return
            }
            // Going back down, possibly from halfway up: whichever, the rise is over.
            this.endRise()
            this.heldPose = pose
            // A body caught halfway up and put back down does not deserve the whole fall again --
            // it is already on the floor as far as the viewer is concerned.
            this.playHeldPoseEntry = animate && !alreadyHeld
            this.activateAnimation(HELD_POSE_ANIMATION[pose], true)
            return
        }

        if (!this.heldPose || this.rising) {
            // Nothing to let go of, or already on the way up.
            return
        }
        this.beginRise()
    }

    /**
     * Start getting up: the pose's own animation, played backwards.
     *
     * `heldPose` deliberately stays set until this lands. Every guard that keeps a fallen body on
     * the ground -- the walk/idle driver, the facing change, a late bake, an attack -- is written
     * against `heldPose`, and a body halfway up needs all of them just as much as one lying still.
     */
    private beginRise = () => {
        const animation = this.animations[HELD_POSE_ANIMATION[this.heldPose]]?.animation
        if (!animation) {
            // The pose never baked (a sheet that failed, a race with no `hurt` row). There is no
            // collapse on screen to reverse, so stand up at once rather than wait for nothing.
            this.releaseHeldPose()
            return
        }
        this.rising = true
        this.riseRestoreOnComplete = animation.onComplete
        animation.onComplete = () => this.releaseHeldPose()
        this.animations[HELD_POSE_ANIMATION[this.heldPose]].playReverse()
    }

    /** Stop rising without standing up -- the sign and the borrowed callback both go back. */
    private endRise = () => {
        if (!this.rising) {
            return
        }
        this.rising = false
        const animation = this.heldPose && this.animations[HELD_POSE_ANIMATION[this.heldPose]]?.animation
        if (animation) {
            animation.animationSpeed = Math.abs(animation.animationSpeed)
            animation.onComplete = this.riseRestoreOnComplete
        }
        this.riseRestoreOnComplete = undefined
    }

    /** The rise has landed (or been cut short): the body is standing and free to act again. */
    private releaseHeldPose = () => {
        this.endRise()
        this.heldPose = undefined
        // Idle rather than walk: doMovement picks the walk up again on the next frame this body
        // actually moves, and guessing otherwise would slide a standing character.
        this.activateAnimation(`idle-${this.facingDir}` as AnimationDictionaryType, true)
    }

    activateAnimation = (updatedAction: AnimationDictionaryType, force?: boolean) => {
        if (!this.initialized) {
            return
        }

        // A held pose OUTRANKS every other source of animation, not just the walk/idle driver that
        // doMovement already returns before. The one that bites without this is a late bake: a walk
        // requested a moment before the body went down lands asynchronously and activates itself
        // (see doPreparation), standing a fallen character back up seconds after they fell. An
        // attack swing arriving for a body that is on the ground is the same bug with a sword.
        //
        // Releasing the pose still works, because setHeldPose clears `heldPose` before it activates
        // the idle it hands back to.
        if (this.heldPose && updatedAction !== HELD_POSE_ANIMATION[this.heldPose]) {
            return
        }
        // An asynchronously baked old facing or attack may land after a phase change.
        if (this.bodyPose && updatedAction !== this.bodyPose.action) return

        this.logme("->", updatedAction)
        if (updatedAction === this.action && !force) {
            // noop
            return
        }

        // Clean up callbacks for current animation before changing
        this.cleanupCurrentAnimationCallbacks()

        const source = this.animations[`${this.action}`]

        const toUpdate = this.animations[`${updatedAction}`]
        if (!toUpdate) {
            // const currentState = this.animationDictionaryTypeLoadingState[updatedAction]
            // if (currentState === "loading" || currentState === "failed") {
            //     return
            // }
            this.logme(updatedAction, "not present, prepping")
            this.prepare(`${updatedAction}`)
            return
        }

        if (source) {
            // Cancel callbacks before stopping animation
            this.cancelCurrentAnimationCallbacks()
            source.stop()
        }

        // Update action and start new animation
        this.action = updatedAction
        if (this.holdsFinalFrame(updatedAction)) {
            // Snap to the pose rather than replay the fall that put the body there. Consumed here
            // and not at setHeldPose, because the first activation after it typically finds no
            // baked animation yet and returns above having only queued the bake.
            toUpdate?.holdFinalFrame()
        } else {
            toUpdate?.play()
        }
        this.playHeldPoseEntry = false

        // Reapply the current tint: each action's clone was built with whatever color was current
        // when it was baked, so an animation prepared before the last recolor() would otherwise
        // show its stale build-time color when activated.
        if (this.currentColor !== undefined) {
            toUpdate?.recolor(this.currentColor)
        }

        // CRITICAL FIX: Preserve listeners when changing animation directions for the same action
        this.preserveListenersForSameAction(updatedAction)

        // Set up callbacks for new animation
        this.setupNewAnimationCallbacks(updatedAction)

        // CRITICAL FIX: Ensure callbacks are set up even if animation is already loaded
        this.ensureCallbacksAreSetUp(updatedAction)
        this.applyBodyPose()
    }

    private applyBodyPose = () => {
        const pose = this.bodyPose
        if (!pose || this.action !== pose.action) return
        const animation = this.animations[this.action]?.animation
        if (animation) animation.gotoAndStop(Math.min(animation.textures.length - 1,
            pose.frame ?? Math.round(pose.progress * (animation.textures.length - 1))))
    }

    addActionListener = (action: ActionType, actionListener: ActionListener) => {
        const callbackId = randomId()
        const callbackState: CallbackState = {
            entityId: actionListener.entityId,
            callback: actionListener.callback,
            animationName: this.action,
            actionType: action,
            createdAt: Date.now(),
            status: "pending",
        }

        logger.debug(
            { tags: ["callback", "animation"] },
            `Adding action listener: ${callbackId} for action: ${action}, current animation: ${this.action}`,
        )

        this.callbackStates.set(callbackId, callbackState)
        this.activeCallbacks.add(callbackId)

        // Store in existing system for compatibility
        let listeners = this.actionCompleteListeners.get(action)
        if (!listeners) {
            listeners = new IndexedMap<ActionListener>()
            this.actionCompleteListeners.set(action, listeners)
            logger.debug({ tags: ["callback", "animation"] }, `Created new listeners map for action: ${action}`)
        }
        listeners.add({ ...actionListener, callbackId })
        logger.debug(
            { tags: ["callback", "animation"] },
            `Added listener to map. Total listeners for ${action}: ${listeners.size()}`,
        )
    }

    doAttack(entity: Entity, callback?: Callback<void>): void {
        // TEST: Log immediately to verify attack logging is working
        logger.info(
            { tags: ["callback", "animation"] },
            `ATTACK TEST: doAttack called for entity ${entity.entityId}, callback: ${!!callback}, action: ${this.action}`,
        )

        logger.debug(
            { tags: ["callback", "animation"] },
            `doAttack called with callback: ${!!callback}, current action: ${this.action}`,
        )

        // Cancel any existing attack callbacks
        this.cancelAttackCallbacks()

        if (isAttackAnimation(this.action)) {
            logger.debug({ tags: ["callback", "animation"] }, `Already attacking, replacing callback`)
            // If already attacking, replace the callback
            this.replaceAttackCallback(callback)
            return
        }

        if (this.disallowMoveAttackAnimation && isWalkAnimation(this.action)) {
            logger.debug(
                { tags: ["callback", "animation"] },
                `Attack blocked - disallowMoveAttackAnimation and walking`,
            )
            return
        }
        this.prevAction = this.action
        const newAction: AnimationDictionaryType = `${this.attackAction}-${this.facingDir}` as AnimationDictionaryType
        logger.debug({ tags: ["callback", "animation"] }, `Starting attack animation: ${newAction}`)

        if (callback) {
            logger.debug(
                { tags: ["callback", "animation"] },
                `Adding callback for action: ${animationToAction(newAction)}`,
            )
            this.addActionListener(animationToAction(newAction), { entityId: randomId(), callback })
        }
        this.activateAnimation(newAction)
    }

    fragment = (entity: Entity) => {
        let actionToUse = this.action
        if (!this.renderMeta[this.action]) {
            actionToUse = `idle-${this.facingDir}` as AnimationDictionaryType
        }
        if (!this.renderMeta[actionToUse]) {
            return
        }

        const { scale } = this.renderMeta[actionToUse]
        const surface = new Graphics()
        surface.x = this.parent?.x - this.parent.width * 0.5
        surface.y = this.parent?.y - this.parent.height * 0.5
        surface.scale.set(scale)
        surface.rotation = this.container.rotation
        this.parent.parent.addChild(surface)

        const f: Texture = this.animations[`${actionToUse}`]?.fragmentBase()
        if (!f) {
            return
        }
        shatterSprite(surface, f, { cacheKey: entity.appearance?.race })
    }

    // Enhanced callback management methods
    private cleanupCurrentAnimationCallbacks() {
        const currentAction = animationToAction(this.action)
        const listeners = this.actionCompleteListeners.get(currentAction)
        if (listeners) {
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.cancelCallback(callbackId)
                }
            })
            listeners.clear()
        }
    }

    private cancelCurrentAnimationCallbacks() {
        this.currentAnimationCallbacks.forEach(callbackId => {
            this.cancelCallback(callbackId)
        })
        this.currentAnimationCallbacks.clear()
    }

    private setupNewAnimationCallbacks(animationName: AnimationDictionaryType) {
        const action = animationToAction(animationName)
        const listeners = this.actionCompleteListeners.get(action)

        if (listeners?.size() > 0) {
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.currentAnimationCallbacks.add(callbackId)
                }
            })
        }
    }

    private cancelAttackCallbacks() {
        const attackActions = ["thrust", "slash", "shoot"]
        attackActions.forEach(action => {
            const listeners = this.actionCompleteListeners.get(action as ActionType)
            if (listeners) {
                listeners.forEach(listener => {
                    const callbackId = (listener as any).callbackId
                    if (callbackId) {
                        this.cancelCallback(callbackId)
                    }
                })
                listeners.clear()
            }
        })
    }

    private replaceAttackCallback(callback?: Callback<void>) {
        if (!callback) return

        const currentAction = animationToAction(this.action)
        const listeners = this.actionCompleteListeners.get(currentAction)
        if (listeners) {
            // Clear existing callbacks
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.cancelCallback(callbackId)
                }
            })
            listeners.clear()

            // Add new callback
            this.addActionListener(currentAction, {
                entityId: randomId(),
                callback,
            })
        }
    }

    private cancelCallback(callbackId: string) {
        const callbackState = this.callbackStates.get(callbackId)
        if (callbackState && callbackState.status === "pending") {
            callbackState.status = "cancelled"
            this.activeCallbacks.delete(callbackId)
            this.currentAnimationCallbacks.delete(callbackId)
        }
    }

    private executeCallbacks(listeners: IndexedMap<ActionListener>, action: ActionType) {
        listeners.forEach(listener => {
            const callbackId = (listener as any).callbackId

            if (callbackId && this.callbackStates.has(callbackId)) {
                const callbackState = this.callbackStates.get(callbackId)!
                if (callbackState.status === "pending") {
                    try {
                        logger.debug({ tags: ["callback", "animation"] }, `Executing callback for ${callbackId}`)
                        callbackState.callback()
                        callbackState.status = "executed"
                        logger.info(
                            { tags: ["callback", "animation"] },
                            `Callback executed successfully: ${callbackId}`,
                        )
                    } catch (error) {
                        logger.error(
                            { tags: ["callback", "animation"] },
                            `Callback execution failed for ${callbackId}:`,
                            error,
                        )
                        callbackState.status = "cancelled"
                    }
                } else {
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Callback ${callbackId} already ${callbackState.status}`,
                    )
                }

                // Remove from active callbacks
                this.activeCallbacks.delete(callbackId)
                this.currentAnimationCallbacks.delete(callbackId)
            } else {
                logger.warn({ tags: ["callback", "animation"] }, `Callback state not found for ${callbackId}`)
            }

            listeners.remove(listener.entityId)
        })

        logger.debug({ tags: ["callback", "animation"] }, `Callback execution complete for action: ${action}`)
    }

    private cleanupAnimationCallbacks(action: AnimationDictionaryType) {
        const actionType = animationToAction(action)
        const listeners = this.actionCompleteListeners.get(actionType)
        if (listeners) {
            listeners.forEach(listener => {
                const callbackId = (listener as any).callbackId
                if (callbackId) {
                    this.cancelCallback(callbackId)
                }
            })
            listeners.clear()
        }
    }

    private changeDirection(newFacingDir: string) {
        const parts = this.action.split("-")
        const updated = `${parts[0]}-${newFacingDir}` as AnimationDictionaryType

        // A held pose has no directional variants to change TO -- the LPC `hurt` row is front-only,
        // so this would compose `hurt-left` and bake nothing. doMovement already returns before
        // reaching here while a pose is held; this is the guard for any other caller.
        if (isHeldPoseAnimation(this.action)) {
            return
        }

        // Don't change direction during attack animations - let them complete
        if (isAttackAnimation(this.action)) {
            logger.debug(
                { tags: ["callback", "animation"] },
                `Preventing direction change during attack: ${this.action} -> ${updated}`,
            )
            return
        }

        // Transfer callbacks to new direction animation
        this.transferCallbacksToNewAnimation(updated)

        this.activateAnimation(updated)
    }

    private transferCallbacksToNewAnimation(newAnimation: AnimationDictionaryType) {
        const currentAction = animationToAction(this.action)
        const newAction = animationToAction(newAnimation)

        // Move callbacks from current action to new action
        const currentListeners = this.actionCompleteListeners.get(currentAction)
        if (currentListeners) {
            let newListeners = this.actionCompleteListeners.get(newAction)
            if (!newListeners) {
                newListeners = new IndexedMap<ActionListener>()
                this.actionCompleteListeners.set(newAction, newListeners)
            }

            // Transfer all listeners
            currentListeners.forEach(listener => {
                newListeners.add(listener)
            })
            currentListeners.clear()
        }
    }

    // Enhanced cleanup method
    cleanup() {
        // Same reasoning as the shadow below: drop the outline's ticker registration and its borrowed
        // texture reference before the generator reclaims the frames.
        this.highlight?.dispose()
        this.highlight = undefined

        // Leave the shared maintenance ticker. Previously each sprite created its own 100ms + 10s
        // intervals that (if never cleared) fired for the rest of the session on every sprite ever
        // built; now the sprite just deregisters and the shared timers stop once the set is empty.
        if (this.registeredWithTicker) {
            lpcSpriteMaintenanceTicker.unregister(this)
            this.registeredWithTicker = false
        }

        // Stop tracking the (about-to-be-abandoned) animation textures and drop the borrowed
        // reference before the generator reclaims them, so the shadow never points at a dead texture.
        if (this.spriteShadowTicking) {
            Ticker.shared.remove(this.updateSpriteShadow)
            this.spriteShadowTicking = false
        }
        if (this.spriteShadow) {
            this.spriteShadow.texture = Texture.EMPTY
        }

        // remove() only detaches the sprite, so the ring has to drop its live-tuning registration
        // here or a paperdoll rebuild would leave the old one registered forever.
        this.playerRing?.dispose()
        this.playerRing = undefined
        // Same reasoning, and it matters more here: a halo also holds the shared breath ticker open,
        // so a leaked registration would leave a dead sprite animating for the rest of the session.
        this.statusHalo?.dispose()
        this.statusHalo = undefined

        // Release every clone this sprite adopted: destroys the clone display objects and drops
        // their cache references so the generator can reclaim baked textures once no live sprite
        // uses that loadout. Guards a late generate callback from re-adopting into a dead sprite.
        this.spriteReleased = true
        // Dispose the display objects of adopted clones (refcount untouched -- keys are released just
        // below). abandon(), not release(), so we don't double-drop the key retained at request time.
        Object.keys(this.animations).forEach(action => {
            lpcCompositeGenerator.abandon(this.animations[action as AnimationDictionaryType])
        })
        this.animations = {} as Record<AnimationDictionaryType, LpcJobResult>
        // Release the request-time key hold for EVERY action we prepared (adopted, still-loading, or
        // failed) -- balances the retainKey in doPreparation exactly once per prepared action.
        this.preparedActionKeys.forEach(key => lpcCompositeGenerator.releaseKey(key))
        this.preparedActionKeys.clear()

        // Cancel all pending callbacks
        this.activeCallbacks.forEach(callbackId => {
            this.cancelCallback(callbackId)
        })

        // Clear all data structures
        this.callbackStates.clear()
        this.activeCallbacks.clear()
        this.currentAnimationCallbacks.clear()
        this.actionCompleteListeners.clear()
    }

    // Override remove so discarding a sprite (paperdoll rebuild / fade-out both call remove(),
    // not destroy()) also tears down its timers and callbacks instead of leaking them.
    remove() {
        this.cleanup()
        super.remove()
    }

    // Override destroy method
    destroy() {
        this.cleanup()
        super.destroy()
    }

    // Periodic cleanup for orphaned callbacks. Public so the shared maintenance ticker can drive it.
    cleanupOrphanedCallbacks() {
        const now = Date.now()
        const maxAge = 30000 // 30 seconds

        this.callbackStates.forEach((state, callbackId) => {
            if (state.status === "pending" && now - state.createdAt > maxAge) {
                console.warn("Cleaning up orphaned callback:", callbackId)
                this.cancelCallback(callbackId)
            }
        })
    }

    // Register for periodic callback maintenance on the shared ticker (idempotent).
    private startCleanupTimer() {
        if (this.registeredWithTicker) {
            return
        }
        this.registeredWithTicker = true
        lpcSpriteMaintenanceTicker.register(this)
    }

    // CRITICAL FIX: Ensure callbacks are set up for animations that are already loaded
    private ensureCallbacksAreSetUp(animationName: AnimationDictionaryType) {
        const action = animationToAction(animationName)
        const listeners = this.actionCompleteListeners.get(action)

        if (listeners?.size() > 0) {
            const animationResult = this.animations[animationName]
            if (animationResult && animationResult.animation && !animationResult.animation.onFrameChange) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `CRITICAL FIX: Setting up missing onFrameChange for ${animationName}`,
                )
                const animation = animationResult.animation
                const totalFrames = animation.totalFrames

                animation.onFrameChange = (frame: number) => {
                    const pct = frame / (totalFrames - 1)
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Frame change (CRITICAL FIX): ${frame}/${totalFrames - 1} (${(pct * 100).toFixed(1)}%)`,
                    )

                    if (pct > 0.5) {
                        logger.debug(
                            { tags: ["callback", "animation"] },
                            `Triggering callbacks (CRITICAL FIX) at ${(pct * 100).toFixed(1)}% completion`,
                        )
                        this.executeCallbacks(listeners, action)
                    }
                }
            }
        }
    }

    // FALLBACK: Check for missing callbacks and set them up. Public so the shared maintenance ticker
    // can drive it; early-returns cheaply when this sprite has no active callbacks.
    checkForMissingCallbacks() {
        if (this.activeCallbacks.size === 0) {
            return // No active callbacks to check
        }

        const currentAction = animationToAction(this.action)
        const listeners = this.actionCompleteListeners.get(currentAction)

        if (listeners?.size() > 0) {
            const animationResult = this.animations[this.action]
            if (animationResult && animationResult.animation && !animationResult.animation.onFrameChange) {
                logger.info(
                    { tags: ["callback", "animation"] },
                    `FALLBACK: Detected missing onFrameChange for ${this.action}, setting up now`,
                )
                const animation = animationResult.animation
                const totalFrames = animation.totalFrames

                animation.onFrameChange = (frame: number) => {
                    const pct = frame / (totalFrames - 1)
                    logger.debug(
                        { tags: ["callback", "animation"] },
                        `Frame change (FALLBACK): ${frame}/${totalFrames - 1} (${(pct * 100).toFixed(1)}%)`,
                    )

                    if (pct > 0.5) {
                        logger.debug(
                            { tags: ["callback", "animation"] },
                            `Triggering callbacks (FALLBACK) at ${(pct * 100).toFixed(1)}% completion`,
                        )
                        this.executeCallbacks(listeners, currentAction)
                    }
                }
            }
        }
    }

    // CRITICAL FIX: Preserve listeners when changing animation directions for the same action
    private preserveListenersForSameAction(newAnimation: AnimationDictionaryType) {
        const newAction = animationToAction(newAnimation)
        const currentAction = animationToAction(this.action)

        // If we're changing to the same action, preserve listeners
        if (newAction === currentAction) {
            // Don't clear listeners for the same action
            return
        }

        // For different actions, clean up current animation callbacks
        logger.debug(
            { tags: ["callback", "animation"] },
            `Cleaning up callbacks for different action: ${currentAction} -> ${newAction}`,
        )
        this.cleanupCurrentAnimationCallbacks()
    }
}
